import React from 'react';
import { useTranslation } from 'react-i18next';
import { timeValueFrom24h, timeValueTo24h } from '../time';
import type {
  BulkSettlementOption,
  BulkSettlementTransactionCardItem,
} from '../types';
import { getSelectedItemCount } from '../transaction-editor/useBulkSettlementTransactionCards';

type Props = {
  card: BulkSettlementTransactionCardItem;
  canDelete: boolean;
  isToday: boolean;
  cashierOptions: BulkSettlementOption[];
  paymentMethodOptions: BulkSettlementOption[];
  peopleCountOptions: BulkSettlementOption[];
  onChangeCard: (
    cardId: string,
    field: 'time' | 'cashierId' | 'itemType' | 'peopleCount' | 'total',
    value: string | BulkSettlementTransactionCardItem['time'],
  ) => void;
  onChangePaymentMethod: (cardId: string, option: BulkSettlementOption) => void;
  onDeleteCard: (cardId: string) => void;
  onOpenMenuSelector: (cardId: string) => void;
};

const TransactionCard = ({
  card,
  canDelete,
  isToday,
  cashierOptions,
  paymentMethodOptions,
  peopleCountOptions,
  onChangeCard,
  onChangePaymentMethod,
  onDeleteCard,
  onOpenMenuSelector,
}: Props) => {
  const { t } = useTranslation();
  const isMenuItem = card.itemType === 'menu_item';
  const isBulkItem = card.itemType === 'bulk_item';
  const itemCount = getSelectedItemCount(card.selectedItems);

  const paymentValue = card.paymentMethodId
    ? `${card.paymentMethod}:${card.paymentMethodId}`
    : 'cash';

  const handlePaymentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const option = paymentMethodOptions.find(o => o.value === e.target.value);
    if (option) onChangePaymentMethod(card.id, option);
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        {/* Row 1: Payment Method | Time | Person in Charge */}
        <div className="row g-3 mb-3">
          <div className="col-4">
            <label className="form-label">{t('label_payment_method')}</label>
            <select className="form-select" style={{ height: '38px' }} value={paymentValue} onChange={handlePaymentChange}>
              {paymentMethodOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-4">
            <label className="form-label">{t('label_time')}</label>
            <input
              type="time"
              className="form-control"
              style={{ height: '38px' }}
              value={timeValueTo24h(card.time)}
              onChange={e => onChangeCard(card.id, 'time', timeValueFrom24h(e.target.value))}
            />
          </div>

          <div className="col-4">
            <label className="form-label">{t('label_person_in_charge')}</label>
            <select
              className="form-select" style={{ height: '38px' }}
              value={card.cashierId}
              onChange={e => onChangeCard(card.id, 'cashierId', e.target.value)}
            >
              {cashierOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 2: People Count | Item Type | Total */}
        <div className="row g-3">
          <div className="col-4">
            <label className="form-label">{t('label_bulk_settlement_people')}</label>
            {peopleCountOptions.length > 0 ? (
              <select
                className="form-select" style={{ height: '38px' }}
                value={card.peopleCount}
                onChange={e => onChangeCard(card.id, 'peopleCount', e.target.value)}
              >
                <option value="">-</option>
                {peopleCountOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="number"
                className="form-control"
                style={{ height: '38px' }}
                min={1}
                value={card.peopleCount}
                onChange={e => onChangeCard(card.id, 'peopleCount', e.target.value)}
              />
            )}
          </div>

          <div className="col-4">
            <label className="form-label">{t('label_type')}</label>
            <div className="d-flex gap-2">
              <select
                className="form-select" style={{ height: '38px' }}
                value={card.itemType}
                onChange={e => onChangeCard(card.id, 'itemType', e.target.value)}
              >
                <option value="bulk_item">{t('label_bulk_settlement_bulk_item')}</option>
                <option value="menu_item">{t('label_menu_item1')}</option>
              </select>
              {isMenuItem && (
                <button
                  type="button"
                  className="btn text-white text-nowrap"
                  style={{ backgroundColor: '#ff9500', borderColor: '#ff9500', height: '38px' }}
                  onClick={() => onOpenMenuSelector(card.id)}
                >
                  {itemCount > 0
                    ? `${t('label_bulk_settlement_add_items')} (${itemCount})`
                    : t('label_bulk_settlement_add_items')}
                </button>
              )}
            </div>
          </div>

          <div className="col-4">
            <label className="form-label">{t('label_total')}</label>
            <div className="d-flex gap-2">
              <input
                type="text"
                inputMode="decimal"
                className="form-control"
                style={{ height: '38px' }}
                value={card.total}
                placeholder={t('label_bulk_settlement_enter_total')}
                disabled={!isBulkItem}
                onChange={e => onChangeCard(card.id, 'total', e.target.value)}
              />
              <button
                type="button"
                className={!canDelete ? 'btn text-nowrap' : 'btn btn-danger text-nowrap'}
                style={
                  !canDelete
                    ? { backgroundColor: '#f0f0f0', borderColor: '#dee2e6', color: '#adb5bd' }
                    : {}
                }
                disabled={!canDelete}
                onClick={() => onDeleteCard(card.id)}
              >
                {t('btn_delete')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionCard;
